/**
 * Settings → Audit log (docs/plans/ADMIN_SETTINGS_SCOPE_2026_09_29.md, WS-G).
 *
 * Every admin action in one place: who, what, on what, when, from where, and the
 * details each writer recorded (settings changes carry before/after). Filters by
 * admin, action, target and dates; page views are hidden unless asked for.
 *
 * Endpoints (routers/admin_audit_log.py, audit.read):
 *   GET /api/admin/audit-log            · GET /api/admin/audit-log/filters
 *   GET /api/admin/audit-log/export.csv
 */
import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Download, FileText, RotateCcw } from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { adminFetch, adminRequest } from '../../../utils/adminApi';
import { SAVE_FAILED, saveBlob } from '../partners/PartnersShared';
import { errorMessage } from './settingsLogic';
import {
  AuditEntry, AuditFilterOptions, AuditFilters, AuditPage, actionLabel, defaultFilters, detailsSummary,
  filtersToQuery, whoLabel,
} from './auditLogLogic';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 15_000, refetchOnWindowFocus: false } },
});

const control = 'rounded-md border border-gray-200 px-2 py-1 text-xs';

const Row: React.FC<{ e: AuditEntry }> = ({ e }) => {
  const [open, setOpen] = useState(false);
  const summary = detailsSummary(e.details);
  return (
    <>
      <tr className="border-b border-gray-50 align-top hover:bg-gray-50">
        <td className="whitespace-nowrap px-3 py-2 text-gray-500">
          {e.created_at ? new Date(e.created_at).toLocaleString() : '—'}
        </td>
        <td className="px-3 py-2 text-gray-800">{whoLabel(e)}</td>
        <td className="px-3 py-2">
          <span className={e.success === false ? 'text-red-700' : 'text-gray-900'} title={e.action_type}>
            {actionLabel(e.action_type)}
          </span>
          {e.success === false && <span className="ml-1 text-[10px] font-semibold uppercase text-red-600">failed</span>}
        </td>
        <td className="max-w-[16rem] truncate px-3 py-2 font-mono text-[11px] text-gray-600" title={e.target_resource ?? ''}>
          {e.target_resource || e.target_user_id || ''}
        </td>
        <td className="px-3 py-2 text-gray-600">
          {summary || e.error_message ? (
            <button type="button" onClick={() => setOpen(o => !o)} className="flex items-start gap-1 text-left hover:text-gray-900">
              {open ? <ChevronDown className="mt-0.5 h-3 w-3 flex-shrink-0" /> : <ChevronRight className="mt-0.5 h-3 w-3 flex-shrink-0" />}
              <span className="line-clamp-2">{e.error_message || summary}</span>
            </button>
          ) : null}
        </td>
        <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] text-gray-400">{e.ip_address ?? ''}</td>
      </tr>
      {open && (
        <tr className="border-b border-gray-100 bg-gray-50">
          <td colSpan={6} className="px-3 py-2">
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all text-[11px] text-gray-700">
              {JSON.stringify({ details: e.details, error: e.error_message, user_agent: e.user_agent, id: e.id }, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
};

const AuditLogInner: React.FC = () => {
  const [draft, setDraft] = useState<AuditFilters>(() => defaultFilters());
  const [filters, setFilters] = useState<AuditFilters>(draft);
  const [more, setMore] = useState<AuditEntry[]>([]);
  const [cursor, setCursor] = useState<AuditPage['next']>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const options = useQuery({
    queryKey: ['admin', 'audit-log', 'filters'],
    queryFn: () => adminFetch<AuditFilterOptions>('/api/admin/audit-log/filters'),
  });
  const first = useQuery({
    queryKey: ['admin', 'audit-log', filters],
    queryFn: () => adminFetch<AuditPage>(`/api/admin/audit-log?${filtersToQuery(filters)}`),
  });

  useEffect(() => {
    setMore([]);
    setCursor(first.data?.next ?? null);
  }, [first.data]);

  const set = <K extends keyof AuditFilters>(k: K, v: AuditFilters[K]) => setDraft(d => ({ ...d, [k]: v }));
  const apply = () => { setNote(null); setFilters({ ...draft }); };
  const reset = () => { const d = defaultFilters(); setDraft(d); setFilters(d); setNote(null); };

  const loadMore = async () => {
    if (!cursor) return;
    setBusy(true);
    try {
      const page = await adminFetch<AuditPage>(`/api/admin/audit-log?${filtersToQuery(filters, cursor)}`);
      setMore(m => [...m, ...page.items]);
      setCursor(page.next);
    } catch (err) {
      setNote({ ok: false, text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = async () => {
    setBusy(true);
    setNote(null);
    try {
      const res = await adminRequest(`/api/admin/audit-log/export.csv?${filtersToQuery(filters)}`);
      const blob = await res.blob();
      const rows = res.headers.get('X-Rows') ?? '?';
      const truncated = res.headers.get('X-Truncated') === 'true';
      const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? 'admin-audit-log.csv';
      if (!saveBlob(blob, name)) {
        setNote({ ok: false, text: SAVE_FAILED });
        return;
      }
      setNote({ ok: true, text: truncated ? `Exported the newest ${rows} rows (the limit). Narrow the dates for the rest.` : `Exported ${rows} rows.` });
    } catch (err) {
      setNote({ ok: false, text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const items = [...(first.data?.items ?? []), ...more];

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
        <FileText className="h-5 w-5" style={{ color: '#1F6C4F' }} /> Audit log
      </h1>
      <p className="mt-1 text-sm text-gray-500">
        Every admin action: who, what, when and from where. Settings and notification changes record before and after.
      </p>

      <div className="mt-4 flex flex-wrap items-end gap-2 rounded-lg border border-gray-200 bg-white p-3 text-xs">
        <label className="flex flex-col gap-1">
          <span className="text-gray-500">Admin</span>
          <select className={control} value={draft.adminId} onChange={e => set('adminId', e.target.value)}>
            <option value="">Everyone</option>
            {(options.data?.admins ?? []).map(a => (
              <option key={a.id} value={a.id}>{(a.full_name || a.email) + (a.active ? '' : ' (inactive)')}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-gray-500">Action</span>
          <input className={`${control} w-56`} list="audit-actions" value={draft.action}
            onChange={e => set('action', e.target.value)} placeholder="Any (end with * for a prefix)" />
          <datalist id="audit-actions">
            {(options.data?.actions ?? []).map(a => (
              <option key={a.action_type} value={a.action_type}>{`${actionLabel(a.action_type)} (${a.count})`}</option>
            ))}
          </datalist>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-gray-500">Target</span>
          <input className={`${control} w-48`} value={draft.target} onChange={e => set('target', e.target.value)}
            placeholder="user id, setting key…" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-gray-500">From</span>
          <input type="date" className={control} value={draft.from} onChange={e => set('from', e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-gray-500">To</span>
          <input type="date" className={control} value={draft.to} onChange={e => set('to', e.target.value)} />
        </label>
        <label className="flex items-center gap-1 pb-1">
          <input type="checkbox" checked={draft.failuresOnly} onChange={e => set('failuresOnly', e.target.checked)} />
          Failures only
        </label>
        <label className="flex items-center gap-1 pb-1">
          <input type="checkbox" checked={draft.includeAccess} onChange={e => set('includeAccess', e.target.checked)} />
          Include page views
        </label>
        <div className="ml-auto flex gap-2">
          <button type="button" onClick={reset} className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-gray-700 hover:bg-gray-50">
            <RotateCcw className="h-3 w-3" /> Reset
          </button>
          <button type="button" onClick={apply} className="rounded-md px-3 py-1 font-medium text-white" style={{ backgroundColor: '#1F6C4F' }}>
            Apply
          </button>
          <button type="button" disabled={busy} onClick={exportCsv}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            <Download className="h-3 w-3" /> Export CSV
          </button>
        </div>
      </div>

      {note && <div className={`mt-2 text-xs ${note.ok ? 'text-green-700' : 'text-red-600'}`} role="status">{note.text}</div>}
      {first.error && <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{errorMessage(first.error)}</div>}

      <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-xs">
          <thead className="border-b border-gray-100 text-left text-[10px] uppercase tracking-wide text-gray-400">
            <tr>
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">Who</th>
              <th className="px-3 py-2">Action</th>
              <th className="px-3 py-2">Target</th>
              <th className="px-3 py-2">Details</th>
              <th className="px-3 py-2">IP</th>
            </tr>
          </thead>
          <tbody>
            {items.map(e => <Row key={e.id} e={e} />)}
          </tbody>
        </table>
        {first.isLoading && <div className="p-6 text-center text-sm text-gray-500">Loading…</div>}
        {!first.isLoading && !first.error && items.length === 0 && (
          <div className="p-6 text-center text-sm text-gray-500">Nothing matches these filters.</div>
        )}
      </div>

      {cursor && (
        <div className="mt-3 text-center">
          <button type="button" disabled={busy} onClick={loadMore}
            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {busy ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

const AuditLog: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <AuditLogInner />
  </QueryClientProvider>
);

export default AuditLog;
